import { useEffect, useMemo, useState } from "react";
import { api, hr } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Calendar } from "./ui/calendar";
import { Textarea } from "./ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "./ui/command";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import { CalendarIcon, Plus, Trash2, Send, Loader2, ChevronsUpDown, Check, UserSearch, Zap } from "lucide-react";
import { format, eachDayOfInterval, isWeekend } from "date-fns";
import { hr as hrLocale } from "date-fns/locale";
import { isHoliday, isCollectiveDay, setCollectivePeriod, setHolidays } from "../lib/holidays";
import { VACATION_ELIGIBLE_ROLES } from "../lib/roles";
import { toast } from "sonner";

const CALENDAR_YEAR_START = 2026;
const CALENDAR_YEAR_END = 2027;

function workdaysBetween(s, e, opts) {
  if (!s || !e || e < s) return 0;
  const excludeCollective = !!(opts && opts.excludeCollective);
  return eachDayOfInterval({ start: s, end: e }).filter(d => {
    if (isWeekend(d) || isHoliday(d)) return false;
    if (excludeCollective && isCollectiveDay(d)) return false;
    return true;
  }).length;
}

function formatDateRange(from, to, emptyLabel) {
  if (!from) return emptyLabel;
  if (!to) return format(from, "dd.MM.yyyy");
  return `${format(from, "dd.MM.yyyy")} – ${format(to, "dd.MM.yyyy")}`;
}

/**
 * On-behalf vacation request dialog for admin/secretary.
 * Allows selecting any combination of date ranges, and OPTIONALLY accepting
 * the collective vacation period. Mirrors NewRequest.jsx behaviour but lives
 * in a modal triggered from the Employees page.
 *
 * Iter52: dodano `pickEmployee` prop koje pokazuje embedded employee combobox
 *         (Tajništvo otvara dialog bez employee-a, employee se bira u dialogu).
 */
export default function BehalfVacationDialog({ employee: employeeProp, open: openProp, onClose, onSuccess, pickEmployee = false, teamOnly = false, currentUserEmail = null }) {
  const open = pickEmployee ? !!openProp : !!employeeProp;
  const [info, setInfo] = useState(null);
  const [acceptsCollective, setAcceptsCollective] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [range, setRange] = useState({ from: undefined, to: undefined });
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  // Iter52: kada pickEmployee=true, drži se interni izbor
  const [pickedEmployee, setPickedEmployee] = useState(null);
  const [users, setUsers] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Iter52b: one-click flow — auto-complete (kreiraj + odobri + PDF + email)
  const [autoComplete, setAutoComplete] = useState(true);
  // Iter109: placeholder rješenje — tajništvo izdaje bez datuma i bez trošenja fonda
  const [placeholder, setPlaceholder] = useState(false);
  // Iter95: fond dana za odabranog zaposlenika (u čije ime se predaje)
  const [empFund, setEmpFund] = useState(null);
  const [fundLoading, setFundLoading] = useState(false);
  const employee = pickEmployee ? pickedEmployee : employeeProp;

  useEffect(() => {
    if (!open) return;
    api.get("/vacation/info").then(r => {
      setInfo(r.data);
      if (r.data?.collective_start && r.data?.collective_end) {
        setCollectivePeriod(r.data.collective_start, r.data.collective_end);
      }
      if (Array.isArray(r.data?.holidays) && r.data.holidays.length > 0) {
        setHolidays(r.data.holidays);
      }
    }).catch(() => {});
    queueMicrotask(() => {
      setAcceptsCollective(null);
      setPeriods([]);
      setRange({ from: undefined, to: undefined });
      setNote("");
      setPickedEmployee(null);
      setAutoComplete(true);
      setEmpFund(null);
    });
    if (pickEmployee) {
      api.get("/users").then(r => setUsers(r.data.filter(u => {
        if (!VACATION_ELIGIBLE_ROLES.includes(u.role)) return false;
        // Iter91: team-lead vidi SAMO svoj tim
        if (teamOnly && currentUserEmail) {
          return (u.manager_email || "").toLowerCase() === currentUserEmail.toLowerCase();
        }
        return true;
      }))).catch(() => {});
    }
  }, [open, pickEmployee, teamOnly, currentUserEmail]);

  // Iter95: dohvat fonda dana za odabranog zaposlenika
  useEffect(() => {
    if (!open || !employee?.email) { setEmpFund(null); return; }
    setFundLoading(true);
    api.get(`/vacation/fund-of/${encodeURIComponent(employee.email)}`)
      .then(r => setEmpFund(r.data))
      .catch(() => setEmpFund(null))
      .finally(() => setFundLoading(false));
  }, [open, employee?.email]);

  const addPeriod = () => {
    if (!range.from || !range.to) { toast.error("Odaberite početak i kraj perioda"); return; }
    const np = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      start: format(range.from, "yyyy-MM-dd"),
      end: format(range.to, "yyyy-MM-dd"),
    };
    setPeriods([...periods, np]);
    setRange({ from: undefined, to: undefined });
  };

  const removePeriod = (id) => setPeriods(periods.filter(p => p.id !== id));

  // Iter100: kad je kolektivni već iskorišten kroz ranije rješenje, sakri
  // karticu prihvaćanja i tretiraj kao dodatne dane (bez ponovnog kolektivnog).
  const collectiveAlreadyUsedForEmp = !!empFund?.collective_already_used;
  const collectiveEnabled = (info ? info.collective_enabled !== false : true) && !collectiveAlreadyUsedForEmp;
  const collDays = (collectiveEnabled && acceptsCollective) ? (info?.collective_workdays ?? 0) : 0;
  // Iter96/Iter100: dedupe overlap između kolektivnog i dodatnih perioda
  const excludeCollFromAdd = (collectiveEnabled && !!acceptsCollective) || collectiveAlreadyUsedForEmp;
  const addDays = periods.reduce((s, p) => s + workdaysBetween(new Date(p.start), new Date(p.end), { excludeCollective: excludeCollFromAdd }), 0);
  const total = collDays + addDays;

  // Iter109: placeholder — dovoljno je odabrati zaposlenika (bez datuma, bez fonda)
  const canSubmit = !!employee && !busy && (placeholder || (total > 0 && (collectiveEnabled ? acceptsCollective !== null : true)));

  const collectiveRangeLabel = useMemo(() => {
    if (!info) return "—";
    const s = info.collective_start?.split("-").reverse().join(".");
    const e = info.collective_end?.split("-").reverse().join(".");
    return `${s}. – ${e}.`;
  }, [info]);

  const submit = async () => {
    if (!employee) { toast.error("Prvo odaberite zaposlenika"); return; }
    // Iter109: placeholder preskaču validaciju perioda
    if (!placeholder && (acceptsCollective === false || !collectiveEnabled) && periods.length === 0) {
      toast.error("Dodajte barem jedan period godišnjeg odmora");
      return;
    }
    setBusy(true);
    try {
      const payload = placeholder ? [] : periods.map(({ start, end }) => ({ start, end }));
      // Iter52b: auto-complete je dostupan SAMO u pickEmployee modu (Tajništvo)
      // Iter91: team-lead nikada — backend ionako odbija auto_complete za team-lead
      // Iter110: placeholder POŠTUJE `autoComplete` toggle — kad je isključen, ide u
      // "Pregled prije slanja" queue (status=approved) gdje tajništvo može ručno urediti
      // tekst rješenja u PdfPreviewDialogu prije izdavanja.
      const useAuto = pickEmployee && autoComplete && !teamOnly;
      const url = `/vacation/requests/on-behalf/${encodeURIComponent(employee.email)}${useAuto ? "?auto_complete=true" : ""}`;
      const r = await api.post(url, {
        additional_periods: payload,
        accepts_collective: placeholder ? false : (collectiveEnabled ? !!acceptsCollective : false),
        placeholder: !!placeholder,
        note: note || `U ime ${employee.full_name}`,
      });
      const meta = r.data?._auto_complete;
      if (useAuto && meta) {
        const parts = [];
        if (meta.pdf_generated) parts.push("PDF");
        if (meta.email_sent) parts.push("email");
        if (meta.sharepoint_uploaded) parts.push("SharePoint");
        const ok = parts.length > 0 ? `Rješenje izdano (${parts.join(" + ")})` : "Rješenje izdano";
        toast.success(`${ok} · ${employee.full_name} · ${total} radnih dana`);
        if (meta.auto_errors && meta.auto_errors.length > 0) {
          toast.warning(`Upozorenje: ${meta.auto_errors[0]}`);
        }
      } else {
        // Iter110: prilagođena poruka za placeholder u ručnom flow-u
        if (placeholder) {
          toast.success(`Placeholder rješenje kreirano za ${employee.full_name} — otvori Pregled prije slanja i uredi tekst.`);
        } else {
          toast.success(`Zahtjev predan u ime ${employee.full_name} (${total} radnih dana)`);
        }
      }
      onSuccess?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Greška");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
          <DialogTitle className="serif text-2xl">
            {teamOnly
              ? <>Predaj GO zahtjev u ime člana tima</>
              : pickEmployee
                ? <>Izradi GO rješenje za zaposlenika</>
                : <>Zahtjev u ime: <span className="text-accent">{employee?.full_name}</span></>}
          </DialogTitle>
          {employee && (
            <p className="text-xs text-muted-foreground">
              {employee.email}{employee.katedra ? ` · ${employee.katedra}` : ""}
            </p>
          )}
        </DialogHeader>

        <div className="px-6 pb-4 overflow-y-auto flex-1 space-y-4" data-testid="behalf-vacation-form">

          {/* Iter52: employee picker (samo kad pickEmployee=true) */}
          {pickEmployee && (
            <div className="border rounded-md p-4 space-y-2 bg-secondary/30">
              <Label className="text-sm font-medium flex items-center gap-2"><UserSearch className="w-4 h-4"/>Zaposlenik</Label>
              <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={pickerOpen}
                    className="w-full justify-between font-normal"
                    data-testid="behalf-employee-picker"
                  >
                    <span className="truncate">
                      {pickedEmployee
                        ? `${pickedEmployee.full_name} · ${pickedEmployee.email}`
                        : "Pretraži po imenu / prezimenu / katedri…"}
                    </span>
                    <ChevronsUpDown className="w-4 h-4 opacity-50 flex-shrink-0"/>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-[--radix-popover-trigger-width] max-w-md" align="start">
                  <Command
                    filter={(value, search) => {
                      if (!search) return 1;
                      return value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
                    }}
                  >
                    <CommandInput placeholder="Pretraga…" data-testid="behalf-employee-search"/>
                    <CommandList>
                      <CommandEmpty>Nema rezultata.</CommandEmpty>
                      <CommandGroup>
                        {users.map(u => {
                          const value = `${u.full_name} ${u.email} ${u.katedra || ""}`;
                          return (
                            <CommandItem
                              key={u.email}
                              value={value}
                              onSelect={() => {
                                setPickedEmployee(u);
                                setPickerOpen(false);
                              }}
                              data-testid={`behalf-employee-opt-${u.email}`}
                            >
                              <Check className={`w-4 h-4 mr-2 flex-shrink-0 ${pickedEmployee?.email === u.email ? "opacity-100" : "opacity-0"}`}/>
                              <div className="flex-1 min-w-0">
                                <div className="truncate">{u.full_name}</div>
                                <div className="text-[11px] text-muted-foreground truncate">{u.email}{u.katedra ? ` · ${u.katedra}` : ""}</div>
                              </div>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {pickedEmployee && (
                <div className="text-xs text-muted-foreground pl-1">
                  Voditelj: <b>{pickedEmployee.manager_email || "—"}</b>
                </div>
              )}
            </div>
          )}

          {/* Iter95: Fond dana — sakriven u placeholder modu (fond se ne troši) */}
          {employee && !placeholder && (
            <div className="border border-accent/40 bg-accent/5 rounded-md p-4 space-y-2" data-testid="behalf-fund-card">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="font-medium text-sm">
                  Raspoloživi fond dana — <span className="text-accent">{employee.full_name}</span>
                </h3>
                {empFund && (
                  <Badge variant="secondary" data-testid="behalf-fund-available">
                    {empFund.available_for_request ?? empFund.available} radnih dana raspoloživo
                  </Badge>
                )}
              </div>
              {fundLoading && (
                <p className="text-xs text-muted-foreground italic">Učitavanje fonda…</p>
              )}
              {!fundLoading && !empFund && (
                <p className="text-xs text-muted-foreground italic">Fond nije dostupan.</p>
              )}
              {empFund && (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                    <div className="bg-background rounded p-2 border">
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Fond {empFund.current_year}</div>
                      <div className="font-semibold text-sm" data-testid="behalf-fund-total">{empFund.fond}</div>
                    </div>
                    <div className="bg-background rounded p-2 border">
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Iskorišteno</div>
                      <div className="font-semibold text-sm" data-testid="behalf-fund-used">{empFund.used_decided}</div>
                    </div>
                    <div className="bg-background rounded p-2 border">
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Rezervirano (pending)</div>
                      <div className="font-semibold text-sm" data-testid="behalf-fund-pending">{empFund.used_pending}</div>
                    </div>
                    <div className="bg-background rounded p-2 border">
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Raspoloživo</div>
                      <div className="font-semibold text-sm text-accent" data-testid="behalf-fund-avail">{empFund.available_for_request ?? empFund.available}</div>
                    </div>
                  </div>
                  {empFund.prev_year_fond != null && empFund.prev_carryover_total > 0 && (
                    <p className="text-[11px] text-muted-foreground">
                      Prijenos iz {empFund.prev_year}.: <b>{empFund.prev_carryover_available}</b>/{empFund.prev_carryover_total} dana
                      {empFund.prev_carryover_expired
                        ? <> — <span className="text-red-600">istekao {empFund.prev_carryover_expires}</span></>
                        : <> — vrijedi do {empFund.prev_carryover_expires}</>}
                    </p>
                  )}
                  {empFund.collective_reserve > 0 && (
                    <p className="text-[11px] text-muted-foreground">
                      Rezervirano za novi kolektivni GO: <b>{empFund.collective_reserve}</b> dana.
                    </p>
                  )}
                  {total > 0 && total > (empFund.available_for_request ?? empFund.available) && (
                    <p className="text-[11px] text-red-600 font-medium" data-testid="behalf-fund-warning">
                      Traženo {total} dana premašuje raspoloživi fond ({empFund.available_for_request ?? empFund.available}).
                    </p>
                  )}
                  {total > 0 && total <= (empFund.available_for_request ?? empFund.available) && (
                    <p className="text-[11px] text-muted-foreground">
                      Nakon ovog zahtjeva ostaje: <b>{(empFund.available_for_request ?? empFund.available) - total}</b> radnih dana.
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {/* Iter100: banner kad je zaposlenik već iskoristio kolektivni */}
          {collectiveAlreadyUsedForEmp && (
            <div className="border border-blue-300 bg-blue-50/60 rounded-md p-3 text-sm text-blue-900 flex items-start gap-2"
                 data-testid="behalf-collective-already-used">
              <span className="text-lg leading-none">✓</span>
              <div>
                <div className="font-medium">Kolektivni GO je već iskorišten</div>
                <div className="text-xs">
                  Zaposlenik već ima izdato rješenje s prihvaćenim kolektivnim odmorom u tekućoj godini. Ovaj zahtjev može sadržavati samo dodatne pojedinačne dane iz preostalog fonda.
                </div>
              </div>
            </div>
          )}

          {/* Iter109: Placeholder mode — tajništvo (pickEmployee, ne teamOnly) */}
          {pickEmployee && !teamOnly && employee && (
            <div className="border border-purple-300 bg-purple-50/50 rounded-md p-3" data-testid="behalf-placeholder-card">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={placeholder}
                  onChange={(e) => setPlaceholder(e.target.checked)}
                  data-testid="behalf-placeholder-checkbox"
                />
                <div className="space-y-1">
                  <div className="font-medium text-sm text-purple-900">
                    Placeholder rješenje (bez datuma, ne troši fond)
                  </div>
                  <div className="text-[11px] text-purple-800">
                    Uključi kad želiš izdati <b>rješenje kao dokument</b> u sklopu ostalih zaposlenika, bez unosa konkretnih datuma. Zaposlenik će naknadno s voditeljem dogovoriti dane. Fond dana <b>ostaje netaknut</b>, a rješenje ne troši kolektivni ni pojedinačne dane.
                    <br/>Nakon izdaje moći ćeš u <em>Pregled prije slanja</em> urediti tekst rješenja (točke I - IV) i tek onda poslati e-mail.
                  </div>
                </div>
              </label>
            </div>
          )}

          {/* Step 1: collective decision (samo kad je postavka aktivna i nije placeholder) */}
          {collectiveEnabled && !placeholder && (
          <div className="border border-accent/30 bg-accent/5 rounded-md p-4 space-y-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <h3 className="font-medium text-sm">Kolektivni godišnji odmor ({collectiveRangeLabel})</h3>
              <Badge variant="secondary">{info?.collective_workdays ?? "—"} radnih dana</Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              Označite želi li zaposlenik koristiti i kolektivni odmor. Ako želi samo pojedinačne dane, odaberite &quot;Ne prihvaća&quot;.
            </p>
            {acceptsCollective === null && (
              <div className="flex flex-wrap gap-2 pt-1">
                <Button size="sm" onClick={() => setAcceptsCollective(true)} data-testid="behalf-accept-coll">
                  Prihvaća kolektivni
                </Button>
                <Button size="sm" variant="outline" onClick={() => setAcceptsCollective(false)} data-testid="behalf-reject-coll">
                  Ne prihvaća — samo pojedinačni dani
                </Button>
              </div>
            )}
            {acceptsCollective === true && (
              <div className="flex items-center gap-3 pt-1">
                <Badge>Uključeno u zahtjev</Badge>
                <button className="text-xs text-muted-foreground underline" onClick={() => setAcceptsCollective(null)}>
                  Promijeni
                </button>
              </div>
            )}
            {acceptsCollective === false && (
              <div className="flex items-center gap-3 pt-1">
                <Badge variant="outline">Bez kolektivnog — samo odabrani dani</Badge>
                <button className="text-xs text-muted-foreground underline" onClick={() => setAcceptsCollective(null)}>
                  Promijeni
                </button>
              </div>
            )}
          </div>
          )}

          {/* Step 2: pick individual periods — sakriveno za placeholder rješenje */}
          {!placeholder && (
          <div className="border rounded-md p-4 space-y-3">
            <div>
              <h3 className="font-medium text-sm">Pojedinačni dani / periodi</h3>
              <p className="text-xs text-muted-foreground">
                Odaberite jedan ili više perioda. Za jedan dan, kliknite isti datum dva puta (od = do).
              </p>
            </div>

            {periods.length > 0 && (
              <div className="space-y-1.5">
                {periods.map((p, i) => (
                  <div key={p.id} className="flex items-center justify-between bg-secondary/60 rounded px-3 py-1.5"
                       data-testid={`behalf-period-${i}`}>
                    <span className="text-sm">
                      <b>{hr(p.start)} – {hr(p.end)}</b>
                      {(() => {
                        const raw = workdaysBetween(new Date(p.start), new Date(p.end));
                        const eff = workdaysBetween(new Date(p.start), new Date(p.end), { excludeCollective: excludeCollFromAdd });
                        const insideColl = raw - eff;
                        return (
                          <>
                            <span className="text-muted-foreground ml-2">
                              ({eff} radnih dana)
                            </span>
                            {excludeCollFromAdd && insideColl > 0 && (
                              <span className="text-[11px] text-blue-700 ml-2" data-testid={`behalf-period-coll-overlap-${i}`}>
                                — {insideColl} dan{insideColl === 1 ? "" : "a"} unutar kolektivnog (ne broji se)
                              </span>
                            )}
                          </>
                        );
                      })()}
                    </span>
                    <Button size="icon" variant="ghost" onClick={() => removePeriod(p.id)}
                            data-testid={`behalf-remove-${i}`}>
                      <Trash2 className="w-4 h-4"/>
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="behalf-pick-range">
                    <CalendarIcon className="w-4 h-4 mr-2"/>
                    {formatDateRange(range.from, range.to, "Odaberi period iz kalendara")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="range"
                    selected={range}
                    onSelect={setRange}
                    numberOfMonths={2}
                    locale={hrLocale}
                    disabled={(d) => excludeCollFromAdd && isCollectiveDay(d)}
                    dayTooltip={(d) => {
                      if (excludeCollFromAdd && isCollectiveDay(d)) return "Unutar kolektivnog GO — automatski uključeno u zahtjev";
                      if (isHoliday(d)) return "Blagdan (ne troši fond)";
                      if (isCollectiveDay(d)) return "Unutar razdoblja kolektivnog GO";
                      return "";
                    }}
                    modifiers={{ holiday: isHoliday, collective: isCollectiveDay }}
                    modifiersClassNames={{
                      holiday: "bg-red-100 text-red-700 font-semibold",
                      collective: "bg-blue-100 text-blue-700",
                    }}
                    fromYear={CALENDAR_YEAR_START}
                    toYear={CALENDAR_YEAR_END}
                    defaultMonth={new Date()}
                  />
                </PopoverContent>
              </Popover>
              <Button size="sm" variant="secondary" onClick={addPeriod} data-testid="behalf-add-period">
                <Plus className="w-4 h-4 mr-1"/>Dodaj period
              </Button>
              <div className="text-[11px] text-muted-foreground flex items-center gap-3 ml-auto">
                <span className="inline-flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded-sm bg-blue-100 border border-blue-300"/>Kolektivni
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded-sm bg-red-100 border border-red-300"/>Blagdan
                </span>
              </div>
            </div>
          </div>
          )}

          {/* Note */}
          <div className="space-y-1.5">
            <h3 className="font-medium text-sm">Napomena (neobvezno)</h3>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={`U ime ${employee?.full_name || "zaposlenika"} — npr. razlog korištenja, kontakt zamjene…`}
              rows={2}
              data-testid="behalf-note"
            />
          </div>

          {/* Summary */}
          <div className="bg-primary text-primary-foreground rounded-md p-3 flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="text-[11px] uppercase opacity-70 tracking-wider">Sažetak</div>
              <div className="text-sm serif mt-0.5">
                {placeholder
                  ? <>Placeholder rješenje — <span className="text-accent font-semibold">bez datuma</span> · fond ostaje netaknut</>
                  : (collectiveEnabled
                      ? <>Kolektivni: <b>{collDays}</b> + Pojedinačni: <b>{addDays}</b> = <span className="text-accent font-semibold">{total} radnih dana</span></>
                      : <>Pojedinačni dani: <span className="text-accent font-semibold">{total} radnih dana</span></>)}
              </div>
            </div>
            <div className="text-[11px] opacity-70">
              {employee
                ? (autoComplete && pickEmployee
                    ? <>Rješenje će biti odmah izdano i poslano: {employee.email}</>
                    : (placeholder && pickEmployee
                        ? <>Rješenje ide u <b>Pregled prije slanja</b> — tekst se uređuje prije izdavanja</>
                        : <>Zahtjev se šalje voditelju: {employee.manager_email || "—"}</>))
                : <span className="italic">Odaberite zaposlenika</span>}
            </div>
          </div>

          {/* Iter52b: one-click auto-complete toggle (samo u Tajništvo modu).
              Iter91: sakrivamo za team-lead flow — voditelj ne generira PDF sam. */}
          {pickEmployee && !teamOnly && (
            <label className="flex items-start gap-3 p-3 rounded border bg-accent/5 border-accent/30 cursor-pointer hover:bg-accent/10 transition-colors" data-testid="auto-complete-toggle-wrap">
              <Checkbox
                checked={autoComplete}
                onCheckedChange={(v) => setAutoComplete(v === true)}
                data-testid="auto-complete-toggle"
                className="mt-0.5"
              />
              <div className="flex-1 text-xs">
                <div className="font-medium flex items-center gap-1.5">
                  <Zap className="w-3.5 h-3.5 text-accent"/>
                  Odmah izdaj rješenje (auto-suglasnost + generiraj PDF + pošalji email)
                </div>
                <p className="text-muted-foreground mt-0.5 leading-relaxed">
                  {autoComplete
                    ? "Tajništvo automatski daje suglasnost, generira PDF s auto-KLASA/URBROJ i šalje email zaposleniku (+ voditelju u CC). Bez slanja zahtjeva voditelju na suglasnost."
                    : "Klasični tok: zahtjev se šalje voditelju na suglasnost, kasnije ručno generiranje PDF-a i slanje."}
                </p>
              </div>
            </label>
          )}

        </div>

        {teamOnly && (
          <div className="px-6 pb-2 text-[11px] text-muted-foreground italic">
            Zahtjev odmah dobiva suglasnost činom predaje i odlazi tajništvu na izradu rješenja.
          </div>
        )}
        <DialogFooter className="px-6 py-4 border-t shrink-0 bg-background">
          <Button variant="outline" onClick={onClose} data-testid="behalf-cancel">Odustani</Button>
          <Button onClick={submit} disabled={!canSubmit} data-testid="behalf-submit">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/>
                  : (pickEmployee && autoComplete && !teamOnly ? <Zap className="w-4 h-4 mr-2"/> : <Send className="w-4 h-4 mr-2"/>)}
            {teamOnly
              ? "Predaj u tajništvo (suglasnost dana)"
              : (pickEmployee && autoComplete
                  ? "Izdaj rješenje (1-klik)"
                  : (placeholder && pickEmployee ? "Kreiraj — uredi tekst u Pregledu" : "Predaj zahtjev"))}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
